/*
 * Sizing rules for the wattage calculator (sections/lp-power-calculator.liquid).
 * Plain script, no dependencies: exposes window.FPCalc. Tested in tests/power-calc.test.js.
 *
 *   running       = sum of running watts x qty
 *   largest surge = max of (starting - running) over checked items; qty doesn't multiply it
 *   peak          = running + largest surge
 *   a model fits  = running <= 85% of its running watts and peak <= its starting watts
 */
(function (root) {
  var USE_CASES = ['rv', 'home', 'tailgating', 'camping', 'jobsite'];
  var RV_HVAC_MIN_START = 3300;
  // Two top units on the 12000W parallel kit.
  var PARALLEL_MAX_START = 12000;
  var PARALLEL_MAX_RUN = 11000;
  // One headroom rule site-wide (RV landing page brief, section 7.2).
  var RUN_HEADROOM = 0.85;
  var LP_FACTOR = 0.9;
  var NEAR_LIMIT = 0.9;

  function fits(running, peak, run, start) {
    return running <= run * RUN_HEADROOM && peak <= start;
  }

  function totals(items) {
    var running = 0;
    var largestSurge = 0;
    var surgeItem = null;
    items.forEach(function (i) {
      var qty = i.qty || 1;
      running += i.running * qty;
      var surge = Math.max(0, i.starting - i.running);
      if (surge > largestSurge) { largestSurge = surge; surgeItem = i.name; }
    });
    return { running: running, peak: running + largestSurge, largestSurge: largestSurge, surgeItem: surgeItem };
  }

  // One model per starting-watt tier: the one preferred for this use case, else the default one.
  function tiers(models, useCase) {
    var byStart = {};
    models.forEach(function (m) {
      var current = byStart[m.start];
      var preferred = m.preferFor && m.preferFor.indexOf(useCase) !== -1;
      var isDefault = !m.preferFor || !m.preferFor.length;
      if (!current) { byStart[m.start] = { model: m, preferred: preferred }; return; }
      if (preferred && !current.preferred) byStart[m.start] = { model: m, preferred: true };
      else if (!current.preferred && isDefault) byStart[m.start] = { model: m, preferred: false };
    });
    return Object.keys(byStart).map(function (k) { return byStart[k].model; })
      .sort(function (a, b) { return a.start - b.start; });
  }

  function fitFor(running, peak, m) {
    if (peak * 100 > m.start * 95 || running * 100 > m.run * 85) return 'tight';
    if (peak * 100 <= m.start * 80) return 'comfortable';
    return 'good';
  }

  function pick(load, models) {
    var running = load.running;
    var peak = load.peak;
    if (!peak) return { type: 'empty' };
    var ladder = tiers(models, load.useCase);
    if (!ladder.length) return { type: 'empty' };
    var minStart = load.useCase === 'rv' && load.hasHvac ? RV_HVAC_MIN_START : 0;

    for (var i = 0; i < ladder.length; i++) {
      var m = ladder[i];
      if (m.start < minStart || !fits(running, peak, m.run, m.start)) continue;
      var fit = fitFor(running, peak, m);
      var next = ladder[i + 1] || null;
      return {
        type: 'single',
        model: m,
        fit: fit,
        pct: peak / m.start,
        stepUp: fit === 'tight' ? next : null,
        suggestParallel: fit === 'tight' && !next,
        note30a: load.useCase === 'rv' && !m.has30a,
        dual: load.useCase === 'home' && !!m.dualVariant
      };
    }

    var top = ladder[ladder.length - 1];
    if (peak <= PARALLEL_MAX_START && running <= PARALLEL_MAX_RUN) {
      return { type: 'parallel', model: top, maxStart: PARALLEL_MAX_START };
    }
    return { type: 'over', model: top };
  }

  // RV landing pages: one model per variant, filtered by the shopper's fuel and start answers.
  // load: {running, peak}; prefs: {fuel: 'gas'|'propane'|'either'|'', start: 'pull'|'electric'|'',
  // dualOnly: bool (Page 2)}; models: [{key, start, run, fuel: 'gas'|'dual', startType: 'pull'|'electric'}].
  // The 3300 tier goes to the gas/pull model when gas and pull are both fine; bigger tiers go gas only on "gas".
  function pickRv(load, models, prefs) {
    prefs = prefs || {};
    var running = load.running;
    var peak = load.peak;
    if (!peak) return { type: 'empty' };
    var propane = prefs.fuel === 'propane';
    var gasOk = prefs.fuel !== 'propane';
    var pullOk = prefs.start !== 'electric';
    var factor = propane ? LP_FACTOR : 1;

    var starts = [];
    models.forEach(function (m) { if (starts.indexOf(m.start) === -1) starts.push(m.start); });
    starts.sort(function (a, b) { return a - b; });

    function inTier(start) {
      return models.filter(function (m) { return m.start === start && (pullOk || m.startType !== 'pull'); });
    }
    function variantFor(start) {
      var tier = inTier(start);
      var gas = tier.filter(function (m) { return m.fuel === 'gas'; })[0] || null;
      var dual = tier.filter(function (m) { return m.fuel === 'dual'; })[0] || null;
      if (propane || prefs.dualOnly) return { model: dual, cheaper: prefs.dualOnly && prefs.fuel === 'gas' ? gas : null };
      var wantGas = gas && gas.startType === 'pull' ? gasOk : prefs.fuel === 'gas';
      return { model: (wantGas ? gas : dual) || gas || dual, cheaper: null };
    }

    var ruledOut = null;
    for (var i = 0; i < starts.length; i++) {
      var pickI = variantFor(starts[i]);
      var m = pickI.model;
      if (!m) continue;
      var run = m.run * factor;
      var start = m.start * factor;
      if (!fits(running, peak, run, start)) { ruledOut = m; continue; }
      var near = running > run * RUN_HEADROOM * NEAR_LIMIT || peak > start * NEAR_LIMIT;
      var next = null;
      for (var j = i + 1; j < starts.length && !next; j++) next = variantFor(starts[j]).model;
      return {
        type: 'single', model: m, propane: propane, ruledOut: ruledOut, nearLimit: near,
        stepUp: near ? next : null, cheaper: pickI.cheaper
      };
    }
    return { type: 'parallel', model: ruledOut, propane: propane };
  }

  // Share links: base64url of compact JSON {u, i: [[id, qty]], c: [[name, running, starting]]}.
  function encodeSetup(setup) {
    return btoa(unescape(encodeURIComponent(JSON.stringify(setup))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function isWatts(n) { return typeof n === 'number' && n % 1 === 0 && n >= 1 && n <= 10000; }

  function decodeSetup(str) {
    if (!str || !/^[A-Za-z0-9_-]+$/.test(str)) return null;
    try {
      var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
      var s = JSON.parse(decodeURIComponent(escape(atob(b64))));
      if (!s || USE_CASES.indexOf(s.u) === -1 || !Array.isArray(s.i)) return null;
      var okItems = s.i.every(function (p) {
        return Array.isArray(p) && typeof p[0] === 'string' && p[1] % 1 === 0 && p[1] >= 1 && p[1] <= 9;
      });
      var okCustom = !s.c || (Array.isArray(s.c) && s.c.every(function (c) {
        return Array.isArray(c) && typeof c[0] === 'string' && isWatts(c[1]) && isWatts(c[2]);
      }));
      return okItems && okCustom ? { u: s.u, i: s.i, c: s.c || [] } : null;
    } catch (e) {
      return null;
    }
  }

  var FIT_TEXT = {
    comfortable: ['Comfortable fit', 'Plenty of headroom for one more appliance.'],
    good: ['Good fit', 'Right-sized. Stagger your big loads.'],
    tight: ['Tight fit', 'It works, but it’s close to the limit.'],
    parallel: ['Two units, one parallel kit', 'That’s more than one unit can start, so two run together on a parallel kit.']
  };
  function watts(n) { return n.toLocaleString('en-US') + ' W'; }

  // "Email me my results": the Klaviyo event. Templates read event.* (the *_text fields are ready to
  // print, since Klaviyo can't format numbers); segments read the profile's calc_*.
  // o: {calcId, useCase, useCaseLabel, items: [{name, qty, running, starting, flags}], totals, fit,
  //     model: {key, name, start, variantId, img, price (cents)}, shareUrl, shopUrl, pageUrl, utm, date}
  function resultsEvent(o) {
    var flags = [];
    o.items.forEach(function (i) {
      (i.flags || []).forEach(function (f) { if (flags.indexOf(f) === -1) flags.push(f); });
    });
    var has = function (f) { return flags.indexOf(f) !== -1; };
    // hvac also covers the furnace fan; the AC tip is only for air conditioners
    var hasAc = o.items.some(function (i) {
      return (i.flags || []).indexOf('hvac') !== -1 && /\bAC\b|air condition/i.test(i.name);
    });
    var img = o.model.img || '';
    var fitText = FIT_TEXT[o.fit] || ['', ''];
    return {
      properties: {
        calc_id: o.calcId,
        use_case: o.useCase,
        use_case_label: o.useCaseLabel,
        model: o.model.key,
        model_name: o.model.name,
        variant: o.model.variantId,
        fit: o.fit,
        peak_w: o.totals.peak,
        running_w: o.totals.running,
        largest_surge_item: o.totals.surgeItem || '',
        peak_text: watts(o.totals.peak),
        running_text: watts(o.totals.running),
        model_start_text: o.model.start ? watts(o.model.start) : '',
        spare_text: o.model.start ? watts(o.model.start - o.totals.peak) : '',
        fit_label: fitText[0],
        fit_note: fitText[1],
        has_ac: hasAc,
        items: o.items.map(function (i) {
          return {
            name: i.name, qty: i.qty || 1, running_w: i.running, starting_w: i.starting,
            running_text: watts(i.running), starting_text: watts(i.starting)
          };
        }),
        item_names: o.items.map(function (i) { return i.name + ((i.qty || 1) > 1 ? ' ×' + i.qty : ''); }).join(', '),
        flags: flags,
        share_url: o.shareUrl,
        shop_url: o.shopUrl,
        model_image: img.indexOf('//') === 0 ? 'https:' + img : img,
        model_price: o.model.price / 100,
        model_price_text: (o.model.price / 100).toLocaleString('en-US', {
          style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2
        }),
        page_url: o.pageUrl,
        utm: o.utm || null
      },
      profile: {
        calc_use_case: o.useCase,
        calc_model: o.model.key,
        calc_peak_w: o.totals.peak,
        calc_fit: o.fit,
        calc_has_ac: hasAc,
        calc_has_cpap: has('medical'),
        calc_has_sensitive: has('sensitive'),
        calc_has_heat: has('heat'),
        calc_date: o.date
      }
    };
  }

  // Klaviyo client API calls (public key only, no server). The event always goes; the list
  // subscription only with consent and a list id.
  function klaviyoRequests(o) {
    var base = 'https://a.klaviyo.com/client/';
    var q = '?company_id=' + encodeURIComponent(o.companyId);
    var reqs = [{
      url: base + 'events/' + q,
      body: { data: { type: 'event', attributes: {
        properties: o.event.properties,
        metric: { data: { type: 'metric', attributes: { name: 'Calculator Results Requested' } } },
        profile: { data: { type: 'profile', attributes: { email: o.email, properties: o.event.profile } } }
      } } }
    }];
    if (o.consent && o.listId) {
      reqs.push({
        url: base + 'subscriptions/' + q,
        body: { data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Wattage calculator',
            profile: { data: { type: 'profile', attributes: {
              email: o.email,
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
            } } }
          },
          relationships: { list: { data: { type: 'list', id: o.listId } } }
        } }
      });
    }
    return reqs;
  }

  root.FPCalc = {
    USE_CASES: USE_CASES, totals: totals, pick: pick, pickRv: pickRv, fits: fits, encodeSetup: encodeSetup, decodeSetup: decodeSetup,
    resultsEvent: resultsEvent, klaviyoRequests: klaviyoRequests
  };
})(window);
